// API Configuration
export interface ApiConfig {
  baseUrl: string;
  apiKey: string;
  menuKey: string;
  menuVersion: number;
  partnerKey: string;
  restaurantKey: string;
  /** Terminal ID for cart payment API (e.g. "10066747") */
  terminalId: string;
  /** Brand key for loyalty APIs (e.g. "PANA") */
  brandKey: string;
}

// Current environment - change this to switch between staging and production
const currentEnvironment: string = 'production';

const cmsApiBaseUrl =
  (process.env.REACT_APP_CMS_API_BASE_URL?.replace(/\/$/, '') ||
    'https://sc-cms-api-dev-za.azurewebsites.net');

/** CMS API base URL for kiosk onboarding screens */
export const CMS_ONBOARDING_SCREENS_URL = `${cmsApiBaseUrl}/api/kiosk/onboarding-screens`;
/** CMS settings endpoint prefix (GET /by-key/:key) */
export const CMS_SETTINGS_BY_KEY_BASE_URL = `${cmsApiBaseUrl}/api/content/settings/by-key`;

/** Base API and auth by environment. Business keys are loaded from CMS at app startup. */
const baseApiByEnvironment: Record<string, Pick<ApiConfig, 'baseUrl' | 'apiKey'>> = {
  staging: {
    baseUrl: 'https://api-stage.spurcorp.io/v1.0',
    apiKey: 'cbca11da7d5b4851ad9ae39f8f5136a1',
  },
  production: {
    baseUrl: 'https://api.spurcorp.io/v1.0',
    apiKey: '65e2b1ceb46948d09e0b257494e77cf0',
  },
};

/** Fallback values until CMS settings are loaded. */
const cmsFallbackConfig: Omit<ApiConfig, 'baseUrl' | 'apiKey'> = {
  menuKey: '',
  menuVersion: 0,
  partnerKey: '',
  restaurantKey: '',
  terminalId: '',
  brandKey: '',
};

// Export the current runtime configuration
export const apiConfig: ApiConfig = {
  ...baseApiByEnvironment[currentEnvironment],
  ...cmsFallbackConfig,
};

/** Apply runtime overrides from CMS settings without losing existing defaults. */
export const applyApiConfigOverrides = (overrides: Partial<ApiConfig>): void => {
  Object.assign(apiConfig, overrides);
};

// Helper function to get the menu endpoint URL
export const getMenuEndpoint = (): string => {
  return `${apiConfig.baseUrl}/menus/${apiConfig.menuKey}/${apiConfig.menuVersion}/items`;
};

// Helper function to get API headers
export const getApiHeaders = (): Record<string, string> => {
  return {
    'X-API-Key': apiConfig.apiKey,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
};

// Helper function to get the create cart/order endpoint URL
export const getCreateCartEndpoint = (): string => {
  return `${apiConfig.baseUrl}/carts/orders`;
};

// Helper function to get the add cart payment endpoint URL
export const getAddCartPaymentEndpoint = (cartId: string): string => {
  return `${apiConfig.baseUrl}/carts/orders/${cartId}/payments`;
};

// Helper function to get the submit cart endpoint URL
export const getSubmitCartEndpoint = (cartId: string): string => {
  return `${apiConfig.baseUrl}/carts/orders/${cartId}/submit`;
};

// Helper function to get the payment status endpoint URL
export const getPaymentStatusEndpoint = (paymentId: string): string => {
  return `${apiConfig.baseUrl}/payments/${paymentId}/status`;
};

/** Loyalty discovery endpoint (POST) - validate OTP and get profile. */
export const getLoyaltyDiscoveryEndpoint = (): string => {
  return `${apiConfig.baseUrl}/Customers/Loyalty/Discovery`;
};